import { useEffect, useState } from "react";
import { DownloadSimple, X } from "@phosphor-icons/react";
import { Capacitor } from "@capacitor/core";
import IosInstallSheet from "./IosInstallSheet";

const DISMISS_KEY = "tt:install-banner-dismissed-v1";
const DISMISS_DAYS = 14;

/**
 * Soft Add-to-Home-Screen banner.
 *
 * Shows on mobile only, only to first-time visitors who haven't dismissed
 * it in the last 14 days. Uses the native `beforeinstallprompt` event on
 * Android Chrome (one-tap install). Falls back to a quick how-to popover
 * on iOS Safari since Apple offers no programmatic install.
 *
 * Already-installed PWAs ALWAYS hide the banner (display-mode: standalone).
 */
export default function InstallBanner() {
  const [visible, setVisible] = useState(false);
  // The Android beforeinstallprompt event we can fire on tap.
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    // Only ever show on real mobile, in browser mode (not when launched
    // from the home screen). Never inside the native iOS/Android app —
    // the user already HAS the app.
    if (typeof window === "undefined") return;
    if (Capacitor.isNativePlatform()) return;
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;

    const ua = navigator.userAgent || "";
    const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(ua);
    if (!isMobile) return;

    // Respect prior dismissals for DISMISS_DAYS.
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (raw) {
        const ts = Number(raw);
        if (Number.isFinite(ts) && Date.now() - ts < DISMISS_DAYS * 24 * 3600 * 1000) {
          return;
        }
      }
    } catch {
      // Private mode — fine, just show the banner.
    }

    const isIos = /iPhone|iPad|iPod/i.test(ua);

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };

    if (isIos) {
      // No native install API on iOS — show our help-style banner immediately.
      setVisible(true);
    } else {
      window.addEventListener("beforeinstallprompt", onBeforeInstall);
    }

    const onInstalled = () => dismiss();
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = () => {
    setVisible(false);
    setShowIosHelp(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
  };

  const onInstall = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        await deferredPrompt.userChoice;
      } catch {
        /* user cancelled */
      }
      setDeferredPrompt(null);
      dismiss();
    } else {
      // iOS fallback — show the share-button instructions.
      setShowIosHelp(true);
    }
  };

  if (!visible) return null;

  return (
    <>
      <div
        className="fixed inset-x-0 bottom-0 z-[60] px-3 pb-3 pointer-events-none"
        data-testid="install-banner"
      >
        <div className="pointer-events-auto mx-auto max-w-md rounded-2xl border border-primary/30 bg-card/95 backdrop-blur shadow-lg flex items-center gap-3 p-3">
          <div className="h-10 w-10 rounded-xl bg-primary text-primary-foreground grid place-items-center flex-none">
            <DownloadSimple weight="bold" className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm leading-tight">
              Install TruckTrackr
            </div>
            <div className="text-xs text-muted-foreground leading-tight mt-0.5">
              One tap to add it to your home screen.
            </div>
          </div>
          <button
            type="button"
            onClick={onInstall}
            className="h-9 px-3 rounded-full text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90 flex-none"
            data-testid="install-banner-cta"
          >
            Install
          </button>
          <button
            type="button"
            onClick={dismiss}
            className="h-9 w-9 grid place-items-center text-muted-foreground hover:text-foreground flex-none"
            aria-label="Dismiss install banner"
            data-testid="install-banner-dismiss"
          >
            <X weight="bold" className="h-4 w-4" />
          </button>
        </div>
      </div>

      {showIosHelp && (
        <IosInstallSheet
          open={showIosHelp}
          onClose={() => {
            setShowIosHelp(false);
            dismiss();
          }}
        />
      )}
    </>
  );
}
